/**
 * Product overviews and product-specific tabs (EN).
 * Drafted from the Garmin projects (settings, strings, code); same markup as the guides.
 */
const products = {
    "products": {
        "voidrunner": {
            "tagline": "Arcade space shooter for your wrist: 5 galaxies, 23 bosses, 11 ships and a new build in every run.",
            "description": "Voidrunner is a vertical space shooter made for the watch. Your ship fires on its own, you steer it left and right with the buttons or by touch. Every 30 seconds you pick one of three upgrade cards and build your loadout from 18 weapons and 13 gadgets. Credits, ships, research and pilot ranks stay with you from run to run.",
            "features": [
                "Weapons fire automatically, you only steer: hold UP or DOWN, or use one of three modes in [watch]Touch steering[/watch].",
                "5 galaxies with 23 spheres, from [watch]Earth Orbit[/watch] to the [watch]Whirlpool Core[/watch]. Every sphere ends with its own boss.",
                "Upgrade cards every 30 seconds: 18 weapons and 13 gadgets with 5 levels each.",
                "11 ships with a trait of their own, from the free [watch]Sparrow[/watch] to the [watch]Phoenix[/watch] that comes back once per run.",
                "Lasting progress: [watch]Tuning garage[/watch] for each ship, [watch]Research[/watch], 20 pilot ranks and 16 perks.",
                "Checkpoints: start a galaxy at any sphere you have reached, with free upgrades for the spheres you skip.",
                "[watch]Endless mode[/watch] after a cleared galaxy, with elite enemies and stronger bosses.",
                "Optional [set]World ranking[/set] with your pilot name, overall and for each galaxy."
            ],
            "deviceNotes": [
                "Needs Connect IQ 4.2 or newer. Runs on 72 watches, from Forerunner 165 and vivoactive 5 to fenix 8 and 9, Venu 4 and Instinct 3 AMOLED.",
                "Watches without a touchscreen, such as Forerunner 255 or Enduro 3, are played fully with the buttons.",
                "The game works offline. Only the world ranking needs the Garmin Connect app on your phone.",
                "On the watch the game is in English, German, French, Spanish, Italian and Portuguese. Other watch languages show English."
            ]
        },
        "horizon": {
            "tagline": "A sun icon rises and sets across the face at real sunrise and sunset times, above an arc view you fill with data, weather, tides or air quality.",
            "description": "Horizon moves a sun icon across the screen between sunrise and sunset and shows the moon at night. Below the time, the arc view shows data slots, weather, forecast, moon info, charts, tides or air quality. Around it you place six data views, each with its own icon color.",
            "features": [
                "[b]Sun and moon cross the face[/b] at their real rise and set times; the sun can show the current weather icon instead.",
                "[b]Arc view[/b] below the time: today's weather, daily forecast, Body Battery and stress, move goal rings, 7-day charts or sensor charts.",
                "[b]Second arc view[/b] that switches on a second wrist gesture, 4 seconds after raising the wrist, or at sunrise and sunset (PRO).",
                "[b]Moon info[/b]: moonrise and moonset with compass direction, illumination, apparent size and moon age (PRO).",
                "[b]Tides[/b] with a StormGlass key: tide heights for the coming hours, low and high level and station distance (PRO).",
                "[b]Local air quality[/b] with an OpenWeatherMap key: eight pollutants such as NO2, PM2.5 and O3 as colored circles (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "All views, bars, fonts and colors customizable, with a theme string for day and night and reduced night or idle modes."
            ],
            "deviceNotes": [
                "Instinct 2, 2S, 2X, Crossover, Instinct 3 Solar and Instinct E get a monochrome version with [set]Theme[/set] and [set]Invert views[/set] instead of color lists. Tide and air quality views are not available there.",
                "On AMOLED watches you set the always-on display in [set]Always On (AOD) Options[/set]. On MIP watches [set]Operating mode[/set] offers an alternative mode if the screen blinks."
            ]
        },
        "mission": {
            "tagline": "A three-section layout with a sunline that tracks the sun and moon, and data blocks that change when you raise your wrist a second time.",
            "description": "Instinct Mission splits the screen into a top, a time and a bottom section, each with its own background color. A sunline moves the sun from sunrise to sunset and shows the moon at night. Below it you show sun times, data slots, Training Status, a weather view or sensor charts.",
            "features": [
                "[b]Sunline[/b] that moves the sun from sunrise to sunset and shows the moon at night, also for the southern hemisphere.",
                "[b]Second screen on wrist gesture[/b]: sunline, the area below it and the bottom view switch to a second choice (PRO).",
                "[b]Under the sunline[/b]: sun times, data slots, Training Status, detailed weather view (PRO) or charts such as Pulse Ox and stress.",
                "[b]Tide times[/b] with a StormglassIO key: next tide, next low and next high tide as data fields (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "[b]Four weather sources[/b]: Garmin, OpenWeatherMap, Visual Crossing or Yr, with feels-like temperature, dew point and UV index (PRO).",
                "[b]Compass bearing[/b] during a run as circle bar, and beers earned today as data field (PRO).",
                "All views, circles, fonts and colors customizable, with day/night theme strings and an analogue watch at night (PRO)."
            ],
            "deviceNotes": [
                "Instinct 2, 2S, 2X, Crossover, Instinct 3 Solar and Instinct E get a monochrome version with invert options and [set]Day/Night Background Inverter[/set] instead of color lists and theme string. Air quality and glucose fields are not available there.",
                "On AMOLED watches you set the always-on display in [set]Amoled devices: Full AOD[/set]. Watches with Connect IQ 4.2 or later also get [set]Datafont[/set], a compact weather view and 7-day step and active minute charts."
            ]
        },
        "dinstinct": {
            "tagline": "A compact data face with a circle view, three progress bars and bottom fields that switch when you raise your wrist a second time.",
            "description": "Dinstinct places a top view with a bar, a second top view, a circle view with a ring and three bottom fields with a bar around the time. You fill each slot from over 80 options, from weather and sun times to charts and Body Battery. A custom text and your own hex color let you personalize the face.",
            "features": [
                "[b]Second set on wrist gesture[/b]: the three bottom fields and the bottom bar switch to other data (PRO).",
                "[b]Sun view bar[/b]: the sun travels along a bar between sunrise and sunset, the moon at night (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "[b]Charts in data fields[/b] for heart rate, pressure, elevation, temperature and stress, plus 7-day goal pills for steps and calories.",
                "[b]Compass bearing[/b] during a run as data field or circle bar (PRO).",
                "[b]Activity totals[/b]: weekly, 7-day and monthly distances for running, cycling, swimming and hiking (hiking PRO).",
                "[b]Weather[/b] from Garmin or OpenWeatherMap, plus Atmos air quality and CGM glucose values as data fields.",
                "All fields, bars, fonts and colors customizable, with theme strings, own hex color, custom text and an analogue night watch (PRO)."
            ],
            "deviceNotes": [
                "Instinct 2, 2S, 2X, Crossover, Instinct 3 Solar and Instinct E get a monochrome version with [set]Theme[/set] (light, dark or by day and night) and [set]Invert views[/set]. Air quality, glucose and custom text are not available there.",
                "On AMOLED watches you switch the always-on display in [set]Always On (AOD) Options[/set]. AMOLED watches and models with more memory also offer an analogue watch for night or idle mode (PRO)."
            ]
        },
        "morphius": {
            "tagline": "An analog watch face that turns into a full-screen sun/moon, tide or weather view when you raise your wrist a second time.",
            "description": "Morphius shows analog time with four configurable data circles and two icon slots. A second wrist gesture or a press on the center turns the whole screen into a Morph View, such as sun and moon times, tides or a weather forecast. Depending on your settings it returns on its own or with the next gesture.",
            "features": [
                "[b]Morph views[/b]: a second wrist gesture or a tap on the center turns the analog face into a full-screen view.",
                "[b]Sun/Moon info[/b]: sun and moon on their real arcs, rise and set times and a countdown to golden hour.",
                "[b]Moon details[/b] in the same view: illumination, distance, rise and set direction and moon age.",
                "[b]Tides view[/b] with a Stormglass key: hourly bars for the next 23 hours, low and high values, station distance (PRO).",
                "[b]Weather forecast[/b] for up to 7 days or hour by hour with rain chance and wind (PRO), plus a current weather overview.",
                "[b]Second circle set[/b]: a morph view that switches all four data circles to other values (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "All hands, markers, circles and colors customizable, with theme strings (also for the night) and reduced night or idle modes."
            ],
            "deviceNotes": [
                "On touchscreen watches, a long press on the center switches the view, and a long press on a circle opens the matching Garmin app, for example heart rate or weather.",
                "Instinct models use a light or dark theme and show an extra data circle in the round sub-display. AMOLED watches add [set]Always On (AOD) Options[/set]."
            ]
        },
        "centurion": {
            "tagline": "A three-section data watchface whose bottom section switches between charts, weather, tides and a world map.",
            "description": "Centurion splits the screen into a top, middle and bottom section, each with its own data fields and colors. The bottom section shows three data fields, a sensor chart, the weather view, a tide view or a world map, and a second bottom view switches in by wrist gesture. The data options cover health, activity, weather, sun and moon.",
            "features": [
                "[b]Bottom section switches[/b] between data fields, weather view, tide view, world map with your position or sensor charts.",
                "[b]Second bottom view[/b] when you raise your wrist again (PRO).",
                "[b]Sun and moon arcs[/b] beside the time, with sun and moon moving in real time (PRO).",
                "[b]Tide view[/b] with a StormglassIO key: hourly tide heights with low and high values (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "[b]Moon data[/b]: moonrise, moonset, moon age (PRO), plus bars for daylight, moon time or moon illumination.",
                "[b]7-day charts[/b] for steps, active minutes and calories (PRO) on watches with more memory, besides sensor charts.",
                "All fields, bars, fonts and colors customizable, with theme strings (also for the night) and an analogue idle watch (PRO)."
            ],
            "deviceNotes": [
                "Instinct 2, 2S, 2X, Crossover, 3 Solar and E use their own layout with a circle view, a light or dark theme and a moon info view. Settings there are in English only.",
                "On touchscreen watches with Connect IQ 4.2+, press and hold a data field to open the matching Garmin app, or the time to switch the bottom section. Many AMOLED watches also get [set]Show Background Gradient[/set] (PRO feature) and a text color per section."
            ]
        },
        "vega": {
            "tagline": "Large time digits that fill up like progress bars, with two switch views for weather, tides and charts.",
            "description": "Vega shows the time in large digits that fill with color according to a value you choose, such as battery or steps. Three data fields, three status icon slots and a bar under the time hold more data. Two switch views bring in forecasts, tides, charts or a second data set by touch or wrist gesture.",
            "features": [
                "[b]Digits as progress bars[/b]: hour and minute fill up with steps, Body Battery, daylight or the current tide height (PRO)",
                "[b]Two switch views[/b] (PRO) by touch or wrist gesture: weather, hourly and daily forecast, tide view, charts or a second data set",
                "[b]Tides[/b] with your StormglassIO key: tide view, tide height in the digit bars and a tide trend icon (PRO)",
                "[b]Sun/Moon view[/b] with sun and moon arcs, rise and set times and moon illumination, on watches with more memory",
                "[b]Sun and moon events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, moonrise, moonset, phase and moon age",
                "[b]Weather from four sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr) with feels-like, min/max, wind, rain, UV index (PRO) and dew point",
                "[b]Over 80 data options[/b], including Air Quality Index, CGM glucose (PRO), beers earned (PRO), three date formats and two extra time zones",
                "All data fields, bars and colors customizable, with theme strings for day and night and a circle bar on bigger watches (PRO)"
            ],
            "deviceNotes": [
                "Instinct 2, 2S, 2X, Crossover, 3 Solar and E use their own layout with a circle view and a light or dark theme. Settings there are in English only.",
                "AMOLED watches add [set]Background image type[/set] (PRO feature: gradient, halo or horizontal). On touchscreen watches with Connect IQ 4.2+, press and hold a data field to open the matching Garmin app."
            ]
        },
        "io": {
            "tagline": "Three data sections around fill-up time digits, with a circle bar and a bottom view that switches to tides, forecasts or air quality.",
            "description": "Io arranges data fields in a top, middle and bottom section around the time, with a circle bar at the edge and a bar under the time. The bottom section switches between three data fields and views such as a 24-hour tide chart, weather forecasts, an air quality chart or a sensor chart. A second bottom view comes in when you raise your wrist twice.",
            "features": [
                "[b]Switchable bottom view[/b]: data fields, 24-hour tide chart, weather, hourly or daily forecast or charts; a second view by wrist gesture (PRO)",
                "[b]Live sun and moon line[/b] or a 24-hour tide chart directly under the time (PRO)",
                "[b]Tides[/b] with your StormglassIO key: tide charts plus next tide, next low and next high tide as data fields (PRO)",
                "[b]Air quality and world map views[/b] on watches with more memory; air quality needs an OpenWeatherMap or Visual Crossing key (PRO)",
                "[b]Sun and moon events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, moonrise, moonset, phase and moon age",
                "[b]Weather from four sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr) with feels-like, min/max, wind, rain, UV index (PRO) and dew point",
                "[b]Around 90 data options[/b], including countdown to sleep or wake-up, 7-day strength training, beers earned (PRO) and two extra time zones",
                "All data fields, bars and colors customizable, with circle bar styles, theme strings for day and night and own hex colors (PRO)"
            ],
            "deviceNotes": [
                "On watches with Connect IQ 4.2+ (most AMOLED models and newer MIP models such as fenix 7), [set]Tide data alternative coordinates[/set] (PRO feature) fetches tides for a fixed spot. With a touchscreen, [set]Hour Long Press Action[/set] and [set]Minute Long Press Action[/set] turn off AOD, turn off the display or toggle the bottom view.",
                "Instinct 2, 2S, 2X, Crossover, 3 Solar and E use their own layout with a data circle and a black or white theme. Settings there are in English only."
            ]
        },
        "victus": {
            "tagline": "Victus puts the time in a frame between a top and a bottom data area, and each area can switch to its own second view.",
            "description": "Victus shows the time in a frame with a data area above and below it. Each area shows data fields or switches to weather, forecast, tide, chart or world map views. You set colors, bars and fonts per element or with one theme string.",
            "features": [
                "[b]Two switch views[/b] above and below the time: data fields, weather, forecasts, tide chart, sensor charts, air quality or world map.",
                "[b]Second set on wrist gesture[/b]: raise your wrist again and both areas switch to a second view (PRO).",
                "[b]5-day weather forecast[/b] or the next 9 hours, with temperature curve, rain chance and wind (PRO).",
                "[b]Tides[/b] with a StormglassIO key: 24-hour tide chart, plus next tide, low and high tide fields on newer watches (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "[b]Real-time sun/moon line[/b] above the time (PRO); bars, even inside the digits, can fill with daylight or moon uptime.",
                "[b]Air quality[/b] from Visual Crossing or OpenWeatherMap as data field or 24-hour chart (PRO), or from the Atmos app.",
                "All data fields, bars, fonts and colors customizable, with theme strings for after sunset and own hex colors (PRO)."
            ],
            "deviceNotes": [
                "Newer watches with Connect IQ 4.2 or later add long press actions on touchscreens ([set]Hour Long Press Action[/set], [set]Minute Long Press Action[/set]), [set]Tide data alternative coordinates[/set], background images and 7-day charts. AMOLED watches add [set]Always On (AOD) Options[/set].",
                "Instinct models use their own black and white layout with a [set]Color Theme[/set] that can change between day and night. The Instinct settings are in English only."
            ]
        },
        "zenith": {
            "tagline": "Zenith draws an arc below the time on which the sun, the moon and the planets move between rising and setting in real time.",
            "description": "Zenith shows the time with data fields above it and a sky arc below it. The sun, the moon and seven planets move along the arc between their rise and set times. Below the arc you see data fields or switch to weather, forecast, tide, chart or world map views.",
            "features": [
                "[b]Planets in real time[/b]: sun, moon and seven planets travel the sky arc, shown at night only or all day",
                "[b]Hourly weather forecast[/b] (PRO) with two lines and a background chart: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tides[/b] with your StormglassIO key: 24-hour tide chart plus next tide, next low and next high tide as data fields (PRO)",
                "[b]World map[/b] with your position as coordinates or MGRS (PRO)",
                "[b]Two switch views[/b] by touch or wrist gesture: weather, tides, world map, 7-day steps or calories versus goal, triathlon time or sensor charts",
                "[b]Sun and moon events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, moonrise, moonset, phase and moon age",
                "[b]Weather from five sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr, Open-Meteo), plus Air Quality Index and CGM glucose (PRO) fields",
                "All data fields, bars and colors customizable, with gradient arc and bar, background images (PRO), theme strings and analogue idle mode (PRO)"
            ],
            "deviceNotes": [
                "Newer watches with Connect IQ 4.2 or later add a [set]Color Theme String[/set], long press actions on touchscreens, [set]Tide data alternative coordinates[/set], MGRS coordinates, gradient colors for bar and arc, and background images. AMOLED watches add [set]Always On (AOD) Options[/set] with several burn-in protected patterns.",
                "Instinct models show the planets in a black and white layout with a [set]Color Theme[/set] for day and night and a big moon view. The Instinct settings are in English only."
            ]
        },
        "trio": {
            "tagline": "Trio shows three data values in circle bars around the time, plus an outer circle bar and a side bar you place at any clock hour.",
            "description": "Trio shows the time in the center with one circle bar above it and two below it. An outer circle bar and a side bar show more progress values. The bottom section shows data fields or switches to weather, forecast, air quality, tide, chart or world map views.",
            "features": [
                "[b]Bottom switch view[/b]: data fields with circle bars, weather, forecasts, tide chart, sensor charts, air quality or world map.",
                "[b]Second bottom view[/b] by a second wrist gesture or touch on the digits (PRO).",
                "[b]5-day weather forecast[/b] or the next 9 hours, with temperature curve, rain chance and wind (PRO).",
                "[b]Tides[/b] with a StormglassIO key: 24-hour tide chart and fields for next tide, next low and high tide (PRO).",
                "[b]Sun events[/b]: golden and blue hour, dusk and dawn, nautical dusk and dawn, as time or countdown.",
                "[b]Side bar at any clock hour[/b] plus outer circle bar (PRO), filled with daylight, moon uptime, Body Battery or steps.",
                "[b]Countdown to sleep or wake-up time[/b] and 7-day strength workout time (PRO) as data fields.",
                "All data fields, bars, fonts and colors customizable, with theme strings (also for the night) and own hex colors (PRO)."
            ],
            "deviceNotes": [
                "Newer watches with Connect IQ 4.2 or later add long press actions on touchscreens ([set]Hour Long Press Action[/set], [set]Minute Long Press Action[/set]), [set]Set Custom Colors[/set], background images and 7-day charts. AMOLED watches add [set]Always On (AOD) Options[/set].",
                "Instinct models use their own black and white layout with a [set]Color Theme[/set] for day and night. The Instinct settings are in English only."
            ]
        },
        "basicpro": {
            "tagline": "A clean digital face with large outlined digits, four edge circle bars and six data fields.",
            "description": "Basic Pro centers the time in large digits with separate fill and outline colors. Three data fields sit above and three below the time, and four status slots frame the digits. Four circle bars along the edge show values such as steps or battery.",
            "features": [
                "[b]All sun events[/b]: golden and blue hour, civil, nautical and astronomical dusk and dawn, as clock time or countdown",
                "[b]Tide times[/b] with your StormglassIO key: next tide, next low tide and next high tide as data fields",
                "[b]Weather from five sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr, Open-Meteo) with feels-like, min/max, wind, rain, UV index and dew point",
                "[b]Six progress displays[/b]: four circle bars plus fill-up hour and minute digits, e.g. for steps, Body Battery, daylight or moon uptime",
                "[b]Moon data[/b]: moonrise and moonset, phase with illumination and moon age",
                "[b]Over 100 data options[/b], including MGRS coordinate, activity stats, Air Quality Index, CGM glucose and a countdown to a date",
                "[b]Idle modes[/b] for day and night: middle section only, time only, blank screen or an analogue watch",
                "All data fields, bars and colors customizable, with fill and outline colors, background images, theme strings and an on-watch style menu"
            ],
            "deviceNotes": [
                "Basic Pro does not support fenix 5, Forerunner 245/945 or vivoactive 3. On the fenix 6 series, background images, long-press actions and the on-watch menu are not available."
            ]
        },
        "nevada": {
            "tagline": "A tall digital face with the hour stacked above the minute and data placed right beside the digits.",
            "description": "Nevada Pro stacks large hour and minute digits and fills the space next to them with data. Each digit can have its own fill, outline and background color. Two side bars and four status slots complete the layout.",
            "features": [
                "[b]All sun events[/b]: golden and blue hour, civil, nautical and astronomical dusk and dawn, as clock time or countdown",
                "[b]Tide times[/b] with your StormglassIO key: next tide, next low tide and next high tide as data fields",
                "[b]Weather from five sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr, Open-Meteo) with feels-like, min/max, wind, rain, UV index and dew point",
                "[b]Live values on MIP watches[/b]: heart rate, solar intensity and seconds can update continuously instead of once a minute",
                "[b]Four progress displays[/b]: two side bars plus fill-up hour and minute digits, e.g. for steps, Body Battery, daylight, moon uptime or rain",
                "[b]Over 100 data options[/b], including MGRS coordinate, moon phase, activity stats, Air Quality Index and CGM glucose, with rolling text for long values",
                "[b]Idle modes[/b] for day and night: middle section only, time only, blank screen or an analogue watch",
                "All data fields, bars and colors customizable, with block-style digit backgrounds, ten time fonts, background images and theme strings"
            ],
            "deviceNotes": [
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout with fewer settings.",
                "On fenix 5, Forerunner 245/945 and vivoactive 3 there is no data font selection and no background image."
            ]
        },
        "tactical": {
            "tagline": "A tactical face with a weekday bar above the time, a seconds bar below and up to four switchviews.",
            "description": "Tactical Elite frames the time with a date bar for the weekdays and a bar for the seconds. Three rows of data sit above the time, and the lower part shows switchviews such as weather, world map or tactical position. You cycle through up to four switchviews by long press or wrist gesture.",
            "features": [
                "[b]Up to four switch views[/b], e.g. six extra data fields with a bar or an analog clock with day and moon time indicators",
                "[b]Date and seconds bars[/b] can also show two custom dates, time zones, MGRS, training status, weather location or your own text",
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours and data fields for next high and low tide (StormglassIO key; custom tide location is Pro)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tactical position[/b] view with MGRS, latitude, longitude and altitude, plus a world map that marks your location",
                "[b]History and training charts[/b]: heart rate, pressure, elevation, temperature and stress, 7-day steps, calories and active minutes, triathlon training time (Pro)",
                "Over 100 data field options; fields, bars and colors customizable, with two gradient color sets, background images and a color theme string"
            ],
            "deviceNotes": [
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) only Switchview 1 and 2 are available, and the date bar, extra data fields and gradient colors are missing.",
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout."
            ]
        },
        "senator": {
            "tagline": "An analog-digital hybrid with eight data fields arranged in segments around the dial.",
            "description": "Senator combines analog hands with a digital time, or shows either one alone. Eight data fields fill the segments of the dial, and four status slots sit in the middle row. A second set of eight data fields can replace the first one as a switchview.",
            "features": [
                "[b]Second set of eight data fields[/b], shown as a switch view in the top or bottom half",
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours and data fields for next high and low tide (StormglassIO key; custom tide location is Pro)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]World map[/b] with your position, shown with latitude/longitude or as MGRS coordinate",
                "[b]History and training charts[/b]: heart rate, pressure, elevation, temperature and stress, 7-day activity charts and triathlon training time (Pro)",
                "[b]Long press shortcuts[/b]: switch views, open the app behind a data field or turn off the display; views also switch by wrist gesture",
                "Analog, digital or both, with six hand styles, minute marks, gradient hands, background images and color theme string; all fields and colors customizable"
            ],
            "deviceNotes": [
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) the analog styles, minute marks, the second data set and the second switchviews are not available.",
                "Instinct 2, Instinct 3 and Instinct Crossover with MIP display use a dedicated monochrome layout with analog and digital time."
            ]
        },
        "tomahawk": {
            "tagline": "A tactical face with a configurable circle bar, nine data fields and switchviews in both halves.",
            "description": "Tomahawk places a segmented circle bar with an icon slot in the upper half and frames the face with bars on both sides and at the bottom. Nine data fields surround the digital time. Both halves can switch to views such as weather, forecast, tides or a world map.",
            "features": [
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours, plus data fields for the next tide, next high and next low tide (StormglassIO key needed)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tactical position[/b] view with MGRS, latitude, longitude and altitude, plus a world map that marks your location",
                "[b]History and training charts[/b]: heart rate, pressure, elevation, temperature and stress, 7-day steps, calories and active minutes, triathlon training time",
                "[b]Long press shortcuts[/b]: switch views, open the app behind a data field or turn off the display; views also switch by wrist gesture",
                "[b]Six progress displays[/b]: circle bar, left, right and bottom bars and filling hour and minute digits, e.g. for steps, Body Battery or battery",
                "Over 100 data field options; fields, bars and colors customizable, with three gradient color sets, background images, analog clock and a color theme string"
            ],
            "deviceNotes": [
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) the circle bar type, the analog clock and the second switchviews are not available.",
                "Instinct 2, Instinct 3 and Instinct Crossover with MIP display use a dedicated monochrome layout."
            ]
        },
        "fletcher": {
            "tagline": "A bold, colorful face with a large hour, a smaller minute and digits that fill up with color.",
            "description": "Fletcher shows a large hour next to a smaller minute, both with two-color gradients. The digits can fill up with color to show a progress value. A curved bar next to the minute and an outer circle bar add two more values, and a second set of data fields is one wrist gesture away.",
            "features": [
                "[b]Second data field set[/b]: a second wrist raise or a long press on hour or minute swaps all six fields for six alternates",
                "[b]Tide times[/b] with your StormglassIO key: next tide, next low tide and next high tide as data fields",
                "[b]All sun events[/b]: sunrise, sunset, golden and blue hour, dusk and dawn, nautical dusk and dawn, as clock time or countdown",
                "[b]Moon data[/b]: moonrise and moonset, phase with illumination and moon age",
                "[b]Weather from five sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr, Open-Meteo) with feels-like, min/max, wind, rain, UV index and dew point",
                "[b]Four progress displays[/b]: fill-up hour and minute digits, a curved bar and an outer ring, e.g. for steps, Body Battery or daylight",
                "[b]Over 100 data options[/b], including MGRS coordinate, activity stats, Air Quality Index, CGM glucose and a countdown to a date",
                "All data fields, bars, gradient colors and twelve time fonts customizable, with theme strings and a style menu on the watch"
            ],
            "deviceNotes": [
                "The alternate data field set also works on fenix 5, Forerunner 245/945 and vivoactive 3.",
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout."
            ]
        },
        "iozen": {
            "tagline": "A calm face that draws the paths of sun and moon across a horizon line below the time.",
            "description": "Io Zen shows the time with three data values beside and above it and the day's sun and moon curves underneath. The current positions of sun and moon move along the curves relative to a horizon line. Extra data fields and two half-circle bars add more information when you need it.",
            "features": [
                "[b]Live sun and moon paths[/b] relative to a horizon line, or sun only, moon only or a normal progress bar",
                "[b]Sun events[/b]: sunrise, sunset, golden and blue hour, dusk and dawn, nautical dusk and dawn, as clock time or countdown",
                "[b]Tide times[/b] with your StormglassIO key: next tide, next low and next high tide, optionally for your own coordinates",
                "[b]Moon data[/b]: moonrise and moonset, phase with illumination and moon age",
                "[b]Weather from four sources[/b] (Garmin, OpenWeatherMap, Visual Crossing, Yr) with feels-like, min/max, wind, rain, UV index and dew point",
                "[b]Two half-circle bars[/b] for progress values such as steps, Body Battery, daylight, moon uptime or rain",
                "[b]Over 90 data options[/b], including MGRS coordinate, activity stats, beers earned, two extra time zones and a countdown to a date",
                "All data fields, bars and colors customizable, with gradient colors for time, sun and moon lines, twelve time fonts and theme strings"
            ],
            "deviceNotes": [
                "Io Zen has no long-press actions, no switchviews and no AOD style setting. AMOLED watches use a fixed burn-in protected AOD pattern.",
                "Instinct 2 and Instinct 3 with MIP display are not supported. Instinct 3 AMOLED uses the standard layout."
            ]
        },
        "vanguard": {
            "tagline": "A tactical face with inner and outer circle bars, gradient digits and ten data fields in two rows above and below the time.",
            "description": "Vanguard builds on Tomahawk with a cleaner layout. Two circle bars run around the dial with up to three gradient colors each. Ten data fields sit in two rows above and below the gradient time, and both halves can switch to other views.",
            "features": [
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours, plus data fields for the next tide, next high and next low tide (StormglassIO key needed)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tactical position[/b] view with MGRS, latitude, longitude and altitude, plus a world map that marks your location",
                "[b]History and training charts[/b]: heart rate, pressure, elevation, temperature and stress, 7-day steps, calories and active minutes, triathlon training time",
                "[b]Long press shortcuts[/b]: switch views, open the app behind a data field or turn off the display; views also switch by wrist gesture",
                "[b]Digits that fill up[/b] with color plus inner and outer circle bars show progress such as steps, Body Battery, battery or daylight",
                "Over 100 data field options; fields, bars and colors customizable, with three-color circle bars, gradient digits, analog clock and color theme string"
            ],
            "deviceNotes": [
                "On Forerunner 165/255, vivoactive 5, Venu Sq 2 and older devices the circle bars use fewer gradient colors.",
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) the second switchviews and the analog clock styles are not available."
            ]
        },
        "nightfever": {
            "tagline": "A colorful dot-matrix style face with minute marks, pill bars and digits that fill up with color.",
            "description": "Night Fever shows the time in large dotted digits with two-color gradients. Minute and five-minute marks run around the dial, and two pill bars above and below the time show values in two colors each. A second set of six data fields can be switched in with a wrist gesture.",
            "features": [
                "[b]Alternate set of six data fields[/b], switched in by a second wrist gesture or a long press on the time",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tide times[/b] as data fields: next tide, next high and next low tide (StormglassIO key needed)",
                "[b]Weather data[/b] from Garmin, OpenWeatherMap, Visual Crossing, Yr or OpenMeteo: temperature, wind, rain chance, UV, dew point and more",
                "[b]Five progress displays[/b]: filling hour and minute digits, two pill bars and an outer circle bar, e.g. for steps, Body Battery or battery",
                "[b]Over 100 data field options[/b], incl. weekly and monthly activity totals, extra time zones, countdown to a date, MGRS and air quality",
                "[b]Long press shortcuts[/b]: open the app behind a data field or turn off the display",
                "Fields, bars, minute marks and colors customizable, with two-color gradient digits, twelve time fonts and a color theme string to save your design"
            ],
            "deviceNotes": [
                "Night Fever has no AOD style or operating-mode setting. AMOLED watches use a fixed burn-in protected AOD pattern.",
                "Instinct 2, Instinct 3 and Instinct Crossover with MIP display use a dedicated monochrome layout."
            ]
        },
        "blackhawk": {
            "tagline": "A tactical face with a heart rate chart above the time and a segmented circle bar below it.",
            "description": "Black Hawk shows a heart rate chart with average, minimum and maximum values in the upper half. Below the time, a segmented circle bar with an icon slot and two quarter-circle bars frame the data fields. Both halves can switch to weather, forecast, tide or map views.",
            "features": [
                "[b]Live chart above the time[/b]: heart rate, air pressure, elevation, temperature or stress, or two normal data fields instead",
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours, plus data fields for the next tide, next high and next low tide (StormglassIO key needed)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tactical position[/b] view with MGRS, latitude, longitude and altitude, plus a world map that marks your location",
                "[b]Activity charts[/b] for the last 7 days: steps, calories and active minutes against your goal, plus triathlon training time",
                "[b]Long press shortcuts[/b]: switch views, open the app behind a data field or turn off the display; views also switch by wrist gesture",
                "Over 100 data field options; circle bar, quarter-circle bars, fields and colors customizable, with gradient digits, analog clock and a color theme string"
            ],
            "deviceNotes": [
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) the second switchviews change by second wrist gesture only. The circle bar type, the theme string and the analog clock are not available there.",
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout."
            ]
        },
        "zero": {
            "tagline": "The successor of Instinct Mission, with a crosshair circle bar and a choice of sun, moon or HUD-style displays below the time.",
            "description": "Mission Zero follows Instinct Mission with a layout for full-color watches. A circle bar with an icon slot sits top right, and a sun and moon track or a graphic bar runs below the time. Both halves can switch to full views such as weather, forecast, tides or a world map.",
            "features": [
                "[b]Sun and moon track[/b] below the time with event time or countdown, replaceable by a Chevron, Rangefinder, HUD Sweep or Pulse Cells bar",
                "[b]Hourly weather forecast[/b] as chart or text, with up to three values of your choice: temperature, wind, rain chance, UV, clouds or humidity",
                "[b]Tide chart[/b] for the next 24 hours, plus data fields for the next tide, next high and next low tide (StormglassIO key needed)",
                "[b]Sun and moon events[/b] as time or countdown: sunrise/sunset, golden and blue hour, dusk/dawn incl. nautical and astronomical, moonrise/moonset",
                "[b]Tactical position[/b] view with MGRS, latitude, longitude and altitude, plus a world map that marks your location",
                "[b]History and training charts[/b]: heart rate, pressure, elevation, temperature and stress, 7-day steps, calories and active minutes, triathlon training time",
                "[b]Long press shortcuts[/b]: switch views, open the app behind a data field or turn off the display; views also switch by wrist gesture",
                "Over 100 data field options; all fields, bars and colors customizable, with crosshair circle bar, gradient digits, analog clock and color theme string"
            ],
            "deviceNotes": [
                "On fenix 5/6 series, Forerunner 245/945, vivoactive 3/4, Venu (1st gen) the under-time display, the circle bar type and the analog clock are not available. Switchviews change by second wrist gesture only.",
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout."
            ]
        },
        "firmament": {
            "tagline": "A night-sky face that shows planets, the ISS, meteor showers and aurora activity on an arc below the time.",
            "description": "Firmament draws a sky arc under the time. Sun, moon, planets and ISS passes move along it in real time, and meteor showers and an aurora glow appear when they are active. Data fields sit around the time and under the arc, and the area under the arc switches between up to three views.",
            "features": [
                "[set]Night sky objects display options[/set]: None, night time only or all the time",
                "Planets, sun, moon and ISS passes on the arc, plus meteor showers and an aurora glow when active",
                "[set]Data view under Arc 1[/set], [set]Data view under Arc 2 (Switchview)[/set] and [set]Data view under Arc 3 (Switchview)[/set], cycled by long press or second wrist gesture",
                "[set]Top Half Circle Bar Options[/set] with [set]Half Circle Bar: small Mark Options[/set] and [set]Half Circle Bar: big Mark Options[/set]",
                "Nine data fields and three status slots under the time",
                "Arc colors with up to three gradient colors",
                "The on-watch Style menu also offers the mark options and the view under the arc"
            ],
            "deviceNotes": [
                "The aurora glow and the third view under the arc need newer devices. On fenix 5, Forerunner 245/945 and vivoactive 3 the view under the arc does not switch.",
                "Instinct 2 and Instinct 3 with MIP display use a dedicated monochrome layout."
            ]
        },
        "atmosweather": {
            "tagline": "Detailed weather forecast from Open-Meteo on up to 11 pages, with a choice of weather model.",
            "description": "Atmos Weather shows current weather, a 24-hour chart and a 7-day forecast with hourly detail for each day. Devices with enough memory add pages for the next 6 hours, rain and snow, wind, UV, comfort, frost and all values. You can pick the forecast model, from Best match to ICON D2.",
            "features": [
                "Up to 11 pages. Order or hide them with [set]Page 1[/set] to [set]Page 11[/set].",
                "Tap a day on the [watch]7 Days[/watch] page to see it hour by hour. On button watches press START and select the day.",
                "Choose the forecast in [set]Weather model[/set]: [set]Best match[/set], ICON, ECMWF, GFS, ICON EU or ICON D2.",
                "[watch]Next 6h[/watch] page with precipitation in 15-minute steps where ICON-D2 or HRRR cover your area.",
                "[watch]Warnings[/watch] for black ice, thunder, hail and storm. The app calculates them from forecast thresholds. They are not official warnings.",
                "24h chart with free choice of [set]24h curve A (area)[/set], [set]24h curve B (line)[/set] and [set]24h background band[/set].",
                "Glance with seven layouts in [set]Glance content[/set] and three value complications for watch faces.",
                "Location from your phone or watch GPS, or fixed coordinates with [set]Latitude[/set] and [set]Longitude[/set]."
            ],
            "deviceNotes": [
                "Low-memory watches such as fenix 6 or Forerunner 245 show Current, 24h, 7 Days and Warnings. Instinct models with 1-bit display show Current, 24h, 7 Days and Wind.",
                "Weather data loads through your phone. The glance needs Connect IQ 4 or newer."
            ]
        },
        "atmosair": {
            "tagline": "Air quality index, pollutants, Sahara dust and pollen forecast in a glance and up to 8 pages.",
            "description": "Atmos Air Quality shows the current air quality index with its category, plus forecasts for the next 24 hours and 5 days. Extra pages cover Sahara dust, particles, haze and visibility, pollen and all pollutant values. You choose between the European and the US AQI.",
            "features": [
                "Switch [set]AQI standard[/set] between [set]European AQI[/set] and [set]US AQI[/set], also in the watch menu.",
                "Glance with AQI value, category and color scale.",
                "[watch]24h[/watch] AQI bars and daily minimum and maximum for [watch]5 Days[/watch].",
                "[set]Sahara dust[/set] and [set]Haze / visibility[/set] pages with 24-hour forecast.",
                "[set]Particle comparison[/set] chart with two series of your choice: PM2.5, PM10, dust, UV, O3, NO2, CO, SO2 or aerosol.",
                "[set]Pollen (Europe only)[/set] for alder, birch, grass, mugwort, olive and ragweed.",
                "[set]All values[/set] page with PM10, PM2.5, CO, CO2, NO2, SO2, O3 and CH4.",
                "Complications for watch faces with the AQI and the time of the last update."
            ],
            "deviceNotes": [
                "Low-memory watches such as fenix 6, Forerunner 245 or 55, Instinct 2 or Venu Sq only show Current, 24h and 5-day pages.",
                "Pollen data is only available for Europe. The app loads data through your phone."
            ]
        },
        "radiacodeapp": {
            "tagline": "Live readings from your RadiaCode radiation meter on your Garmin, with alarms and optional activity recording.",
            "description": "Radia Code App connects your Garmin directly to a RadiaCode meter over Bluetooth. It shows dose rate, count rate and a pulse chart against the local background. You can record an activity that stores dose rate, CPM and total dose in Garmin Connect.",
            "features": [
                "Direct Bluetooth connection. No phone and no internet needed.",
                "Dose rate in [set]Sievert (Sv)[/set] or [set]Roentgen (R)[/set], count rate as CPS or CPM.",
                "Pulse chart and color scale relative to the local background level.",
                "[set]Auto Detect Radiation[/set] alerts at about twice the local background.",
                "[set]Threshold Alarm (µSv/h)[/set] with fixed limits from 0.50 to 10.0 µSv/h on the watch.",
                "Activity recording with CPM, dose rate, total dose and lap dose for Garmin Connect.",
                "Reconnects to the last used meter. [watch]Forget device[/watch] turns this off.",
                "Separate data field [watch]RadiaCode DF[/watch] that records the readings inside any activity."
            ],
            "deviceNotes": [
                "Alarms only work while the app is open and connected to the meter. The app does not monitor in the background, and the data field has no alarm. Keep the alarms on the RadiaCode itself switched on.",
                "A RadiaCode connects to one device at a time. Disconnect the RadiaCode phone app before you connect the watch."
            ]
        },
        "bodysurfing": {
            "tagline": "Records bodysurfing sessions and detects every wave you ride by GPS.",
            "description": "BodySurf records a bodysurfing session as a Garmin activity. It detects rides from GPS speed and saves each ride as a lap with distance and time. Every ride gets a title based on how far you went.",
            "features": [
                "Automatic ride detection from GPS speed, with a short vibration at the end of each ride.",
                "BACK starts or ends a ride by hand during the session.",
                "Live screen with [watch]RIDING[/watch] or [watch]Waiting[/watch], ride distance, speed, number of rides and best ride.",
                "Each ride is a lap in Garmin Connect with [watch]Ride Distance[/watch], [watch]Ride Time[/watch] and [watch]Ride Title[/watch].",
                "Session summary with total distance, time, best ride and one page per ride with its maximum speed.",
                "13 ride titles from [watch]Beach Bum[/watch] to [watch]Poseidon[/watch], a new one every 3 m.",
                "Taps on the screen are ignored while recording. Only the START button stops the session.",
                "[watch]Units[/watch] in the watch menu: metric (m, km/h) or imperial (ft, mph)."
            ],
            "deviceNotes": [
                "Needs GPS. Wait for the GPS fix before you press START."
            ]
        },
        "bierwandern": {
            "tagline": "Track your hike and the drinks along the way, with a rough blood alcohol estimate.",
            "description": "Bierwandern records a hike as a Garmin activity and counts the beer, wine and schnapps you have on the way. It shows distance, steps and an estimated blood alcohol level in per mille. The estimate is very rough and must never be used to decide whether you can drive.",
            "features": [
                "Three pages: [watch]OVERVIEW[/watch], [watch]DRINKS[/watch] and [watch]BAC[/watch].",
                "Beer in 0.25 L, 0.33 L and 0.50 L, plus wine and schnapps.",
                "Blood alcohol estimate with the Widmark formula, based on weight and sex from your Garmin profile.",
                "BAC chart and peak value for the whole hike.",
                "Set the alcohol content with [set]Beer alcohol %[/set], [set]Wine alcohol %[/set] and [set]Schnapps alcohol %[/set].",
                "[set]Beer reference size[/set] sets the glass size for the beer count on the overview.",
                "Saves BAC, drink events and drink totals in the activity for Garmin Connect.",
                "[set]Use GPS[/set] turns GPS recording on or off."
            ],
            "deviceNotes": [
                "The BAC value is a very rough estimate. Never use it to decide whether you can drive.",
                "On the watch the app is called Beer Hike, in German Bierwanderung."
            ]
        }
    },
    "features": {
        "tabLabel": "Controls & display",
        "more": "How it works",
        "olderNote": "Some features need a newer watch. On older models such as the fenix 5/6 series, Forerunner 245/945 or vivoactive 3/4 they are missing. [link=/guides/devices]What differs per watch[/link]",
        "req": {
            "touch": "Touchscreen · CIQ 4.2+",
            "ciq42": "CIQ 4.2+",
            "crossover": "Instinct Crossover AMOLED"
        },
        "items": {
            "longPress": {
                "title": "Long-press actions",
                "full": "Assign an action to a long press on the hour, the minute or the icons: switch the top or bottom view, or turn off AOD or the display.",
                "fullNoSwitch": "Assign an action to a long press on the hour, the minute or the icons: turn off AOD or the whole display.",
                "leftIcons": "Assign an action to a long press on the hour, the minute or the left icons: switch the top or bottom view, or turn off AOD or the display.",
                "time": "Assign an action to a long press on the hour or the minute, for example swap the data field set or turn off the display.",
                "timeNoSwitch": "Assign an action to a long press on the hour or the minute: turn off AOD or the whole display.",
                "tactical": "Assign an action to a long press on the hour, the minute or the icons, for example step through the switchviews or turn off the display."
            },
            "openApps": {
                "title": "Open Garmin apps",
                "text": "A long press on a data field opens the matching Garmin app, for example heart rate, steps or weather."
            },
            "switchview": {
                "title": "Second view by wrist gesture",
                "text": "The top and bottom areas can show a second view, for example the forecast chart or the world map. Raise your wrist a second time to switch.",
                "tactical": "Up to four switchviews, from weather and forecast to the tactical position. Each second wrist raise shows the next one."
            },
            "alternateSet": {
                "title": "Second data field set",
                "six": "A second set of six data fields. Swap it in with a second wrist raise or a long press.",
                "senator": "All eight data fields have a second set, which you show as a switchview.",
                "tactical": "Six extra data fields and an extra bar, shown as a switchview."
            },
            "watchMenu": {
                "title": "Menu on the watch",
                "text": "Change theme and fonts with a live preview on the watch, and check the WatchfaceID and lock status."
            },
            "displayModes": {
                "title": "Always-on & power saving",
                "text": "Choose what stays visible when the watch is idle, with a separate setting for the night. AMOLED watches get burn-in protected always-on styles, MIP watches power-saving modes.",
                "noMip": "Choose what stays visible when the watch is idle, with a separate setting for the night. AMOLED watches get burn-in protected always-on styles.",
                "fixed": "On AMOLED watches the face uses a fixed, burn-in protected always-on pattern."
            },
            "handsMove": {
                "title": "Hands move aside",
                "text": "The physical hands move out of the way after you raise your wrist, so they do not cover the display."
            },
            "forecast": {
                "title": "Weather forecast chart",
                "text": "The hourly forecast as two lines over a background chart. You choose the values, for example temperature, rain or wind."
            }
        }
    },
    "ptabs": {
        "voidrunner": {
            "tabLabel": "Game guide",
            "title": "Voidrunner game guide",
            "intro": "Voidrunner is an arcade space shooter for the watch. Your ship fires on its own. You steer, dodge and choose upgrades.",
            "sections": [
                {
                    "id": "controls",
                    "heading": "Controls",
                    "paragraphs": [
                        "There is no fire button, your weapons fire automatically. Every screen works with the buttons alone. On touchscreen watches you can also steer with your finger. Choose the touch mode and vibration under [watch]Settings[/watch]."
                    ],
                    "options": [
                        {
                            "label": "UP / DOWN (hold)",
                            "text": "Fly left / right. Let go to stop."
                        },
                        {
                            "label": "START or BACK",
                            "text": "Pause, then [watch]Resume[/watch] or [watch]Abort run[/watch]."
                        },
                        {
                            "label": "In menus",
                            "text": "UP and DOWN select, START confirms, BACK goes back. Tapping works too."
                        },
                        {
                            "label": "Touch: Target",
                            "text": "Default. Tap or slide: the ship flies to that spot and stays there."
                        },
                        {
                            "label": "Touch: Screen sides",
                            "text": "Hold the left or right half of the screen to fly that way."
                        },
                        {
                            "label": "Touch: Follow ship",
                            "text": "The ship follows your finger while you hold it."
                        }
                    ]
                },
                {
                    "id": "intro",
                    "heading": "How a run works",
                    "list": [
                        "Each sphere has about 90 seconds of enemy waves, then a boss. Beat it to repair a heart and fly on to the next sphere.",
                        "Every 30 seconds the game stops for an upgrade card. The first one comes after 20 seconds.",
                        "Enemies that slip past the line below your ship cost a heart. So do enemy shots and ramming an enemy.",
                        "Gold crystals are credits, green kits repair a heart. Chain kills for a combo of up to ×5.",
                        "At zero hull the run is over. You keep credits, XP, your record and every sphere you reached.",
                        "Every galaxy starts fresh with your start weapon at level 1. Ship, tuning, research and perks come along."
                    ]
                },
                {
                    "id": "hud",
                    "heading": "On screen",
                    "options": [
                        {
                            "label": "Top arc",
                            "text": "Progress through the sphere. During the boss fight it shows the boss hull in red."
                        },
                        {
                            "label": "Bottom arc",
                            "text": "Time until the next upgrade card."
                        },
                        {
                            "label": "Hearts",
                            "text": "Your hull, plus shield charges that each block a hit."
                        },
                        {
                            "label": "Bottom row",
                            "text": "Weapons, gadgets, credits, score and combo."
                        }
                    ]
                },
                {
                    "id": "upgrades",
                    "heading": "Upgrade cards",
                    "paragraphs": [
                        "Choose one of three cards with UP, DOWN and START, or tap it. A card brings a new weapon or gadget, or raises one by a level, up to level 5. [watch]Hull repair[/watch] heals two hearts and comes up more often when you are low. [watch]Salvage[/watch] pays credits, [watch]Reroll[/watch] draws three new cards."
                    ],
                    "list": [
                        "[b]Weapons[/b], 2 to 4 slots, all fire at once: Blaster, Laser, Plasma, Missiles, Spread, Tesla, Railgun, Flak. Every ship you own adds its signature weapon, for example Disc (Sparrow) or Singularity (Titan). Clearing Triangulum unlocks Nova, clearing the Dark Nexus unlocks Flux Wave.",
                        "[b]Gadgets[/b], 2 to 4 slots: Shield, Turrets, Thrusters, Armor, Nanobots, Drones, EMP, Overclock, Wingman, Mirror Image, Orbital Strike, Stasis Field, Ram Squad."
                    ]
                },
                {
                    "id": "galaxies",
                    "heading": "Galaxies and bosses",
                    "paragraphs": [
                        "Beat the last boss of a galaxy to unlock the next one. Every boss has its own attacks and gets faster below 40 % hull. Later galaxies have tougher enemies, but pay more credits and multiply your score."
                    ],
                    "options": [
                        {
                            "label": "Milky Way",
                            "text": "6 spheres, from Earth Orbit to Sagittarius A*. Score 100 %."
                        },
                        {
                            "label": "Andromeda",
                            "text": "5 spheres. Score 150 %."
                        },
                        {
                            "label": "Triangulum",
                            "text": "4 spheres. Score 200 %, reward: Nova."
                        },
                        {
                            "label": "Dark Nexus",
                            "text": "4 spheres. Score 250 %, reward: Flux Wave."
                        },
                        {
                            "label": "Whirlpool",
                            "text": "4 spheres. Score 300 %."
                        }
                    ]
                },
                {
                    "id": "ships",
                    "heading": "Ships",
                    "paragraphs": [
                        "Buy ships in the [watch]Hangar[/watch] once their galaxy is unlocked. Each ship has its own trait, and owning it unlocks its signature weapon for all ships."
                    ]
                },
                {
                    "id": "progress",
                    "heading": "Lasting progress",
                    "options": [
                        {
                            "label": "Tuning garage",
                            "text": "For each ship: hull, speed, damage and fire rate in 5 levels, plus its [watch]Start weapon[/watch]. Press START on your active ship in the Hangar."
                        },
                        {
                            "label": "Research",
                            "text": "For all ships: start hull, damage, credits, rerolls and faster upgrade cards."
                        },
                        {
                            "label": "Perks",
                            "text": "20 pilot ranks from [watch]Cadet[/watch] to [watch]Voidrunner[/watch] unlock 16 perks, such as [watch]Second Wind[/watch] or [watch]Magnet[/watch]. You equip 1 to 5 at a time."
                        },
                        {
                            "label": "Checkpoints",
                            "text": "[watch]Launch[/watch] lets you start at any sphere you have reached, with free upgrades for the spheres you skip."
                        },
                        {
                            "label": "Daily bonus",
                            "text": "The first run each day that earns at least 25 credits pays double ([watch]DAILY x2[/watch])."
                        }
                    ]
                },
                {
                    "id": "endless",
                    "heading": "After a galaxy",
                    "options": [
                        {
                            "label": "Endless mode",
                            "text": "Keep flying with your loadout. Every loop is harder, with elite enemies and stronger bosses."
                        },
                        {
                            "label": "Enter",
                            "text": "Start fresh in the galaxy you just unlocked."
                        },
                        {
                            "label": "Land",
                            "text": "End the run."
                        }
                    ]
                },
                {
                    "id": "ranking",
                    "heading": "World ranking",
                    "paragraphs": [
                        "Optional and off by default. Turn on [set]World ranking[/set] in the game settings or in the Garmin Connect app, and set your [set]Pilot name[/set] there (up to 16 characters). Only your best run in each galaxy is uploaded, through the phone connection. [watch]Records[/watch] shows your top 5 and the world top 10, overall and for each galaxy.",
                        "To count installs, the game sends anonymous data: a hashed device ID, the watch model, the language and the app version. Your name and scores are only sent with the ranking turned on."
                    ]
                },
                {
                    "id": "tips",
                    "heading": "Tips",
                    "list": [
                        "Fill your weapon slots first, levels can come later.",
                        "Stuck on a boss? Spend credits on research and tuning, then start again from the checkpoint.",
                        "Do not close the app in the middle of a run, the run is lost. Pause and choose [watch]Abort run[/watch] to keep credits and XP.",
                        "If dragging your finger stutters on your watch, switch to [watch]Target[/watch] and tap."
                    ]
                },
                {
                    "id": "devices",
                    "heading": "Devices",
                    "paragraphs": [
                        "Needs Connect IQ 4.2 or newer and runs on 72 watches, from Forerunner 165 to fenix 9. The device list in the overview shows yours. Progress is stored on the watch and is not synced to other watches."
                    ]
                }
            ],
            "shipList": {
                "hull": "Hull",
                "speed": "Speed",
                "slots": "Slots",
                "weapons": "weapons",
                "gadgets": "gadgets",
                "free": "Free",
                "cr": "CR",
                "needs": "Needs",
                "galaxies": [
                    "Milky Way",
                    "Andromeda",
                    "Triangulum",
                    "Dark Nexus",
                    "Whirlpool"
                ],
                "ships": [
                    {
                        "trait": "Scavenger:",
                        "text": "+20 % credits. Starter ship, flies with Blaster, unlocks Disc."
                    },
                    {
                        "trait": "Evasive:",
                        "text": "15 % chance to dodge a hit. Blaster, unlocks Flanker."
                    },
                    {
                        "trait": "Lethal:",
                        "text": "12 % critical hits with double damage. Spread, unlocks Venom."
                    },
                    {
                        "trait": "Escorts:",
                        "text": "Two escort fighters fly along and shoot. Blaster."
                    },
                    {
                        "trait": "Shield core:",
                        "text": "Built-in shield that recharges in 22 seconds. Blaster, unlocks Mines."
                    },
                    {
                        "trait": "Phase:",
                        "text": "Twice as long invulnerable after a hit. Laser, unlocks Sweep."
                    },
                    {
                        "trait": "Berserker:",
                        "text": "+15 % damage for every lost heart, up to +75 %. Flak."
                    },
                    {
                        "trait": "Regenerator:",
                        "text": "+1 heart every 30 seconds. Plasma, unlocks Prism."
                    },
                    {
                        "trait": "Plating:",
                        "text": "Hits that cost a full heart only cost half a heart. Missiles, unlocks Singularity."
                    },
                    {
                        "trait": "Hair trigger:",
                        "text": "+15 % fire rate. Tesla, unlocks Ricochet."
                    },
                    {
                        "trait": "Rebirth:",
                        "text": "Comes back once per run with full hull and a blast that hits every enemy on screen. Railgun."
                    }
                ]
            },
            "gallery": [
                "Radiation Kraken · Milky Way",
                "Sand Leviathan · Andromeda",
                "Crystal Hydra · Triangulum",
                "Shadow Twin · Dark Nexus",
                "Maelstrom · Whirlpool",
                "Spore Queen · Whirlpool"
            ]
        },
        "morphius": {
            "tabLabel": "Views",
            "title": "Morph Views",
            "intro": "Morphius can swap the analog dial for a full-screen view. Pick up to two Morph Views in [set]Morph View 1[/set] and [set]Morph View 2[/set].",
            "sections": [
                {
                    "id": "views",
                    "heading": "Available views",
                    "options": [
                        {
                            "label": "Sun/Moon Info",
                            "text": "Sun and moon on a sky arc, rise and set times, the next sun event with a countdown and the moon phase."
                        },
                        {
                            "label": "Tides View (PRO)",
                            "text": "Nearest tide station, bars for the coming hours, low and high tide. Needs a Stormglass key. [link=/guides/tides]Set up tides[/link]"
                        },
                        {
                            "label": "Current Weather Overview",
                            "text": "Temperature, provider, humidity, feels-like temperature, rain chance and wind."
                        },
                        {
                            "label": "Hourly / Daily Weather Forecast (PRO)",
                            "text": "Garmin Weather forecast by hour or by day."
                        },
                        {
                            "label": "Second Circles View Options (PRO)",
                            "text": "The dial stays, the four circles show a second set of data."
                        }
                    ]
                },
                {
                    "id": "switch",
                    "heading": "Switching",
                    "paragraphs": [
                        "A second wrist raise shows the next view. On touchscreen watches a long press on the center does the same. [set]Switchoptions[/set] sets what triggers a switch, [set]MorphViews switching back behaviour[/set] when the dial returns. By default it returns when the display goes to low power. Instinct models switch by wrist gesture only. [link=/guides/switchviews]Switchviews & gestures[/link]"
                    ]
                },
                {
                    "id": "notes",
                    "heading": "Good to know",
                    "paragraphs": [
                        "A long press on one of the four circles opens the matching Garmin app (Connect IQ 4.2 or newer). Sun/Moon, tides and weather need a position. If a view shows [watch]GPS?[/watch], [link=/guides/location]do a GPS fix[/link]."
                    ]
                }
            ]
        },
        "atmosweather": {
            "tabLabel": "App guide",
            "title": "Atmos Weather guide",
            "intro": "Atmos Weather loads forecasts from Open-Meteo and shows them on up to 11 pages. It runs on Garmin watches, Edge bike computers and outdoor handhelds. This guide explains the pages, the controls, the weather models and the settings.",
            "sections": [
                {
                    "id": "pages",
                    "heading": "Pages",
                    "paragraphs": [
                        "You decide which page sits in which slot with [set]Page 1[/set] to [set]Page 11[/set] in the app settings. Set a slot to [set]None (hidden)[/set] to skip it. By default all pages are on.",
                        "If no new data arrives, the app moves along the stored hourly forecast. The values stay current instead of showing an old snapshot."
                    ],
                    "options": [
                        {
                            "label": "Current weather",
                            "text": "Animated weather symbol, temperature, condition, wind with windsock and today's min and max. The footer shows the data age or the most important warning."
                        },
                        {
                            "label": "24h forecast",
                            "text": "Chart of the next 24 hours. You choose what curve A, curve B and the background band show."
                        },
                        {
                            "label": "7-day forecast",
                            "text": "One row per day with symbol, min and max on a shared week scale and drops or flakes for the expected precipitation. Open a day for hourly details."
                        },
                        {
                            "label": "Next 6h (15 min)",
                            "text": "Precipitation as 15-minute bars plus symbol, temperature and wind for each of the next six hours."
                        },
                        {
                            "label": "Rain and snow",
                            "text": "Rain and snow for the next 24 hours, rain chance, daily totals for the week and snow depth."
                        },
                        {
                            "label": "Wind",
                            "text": "Wind rose with direction, speed, gusts and Beaufort, plus the wind and gust curve for 24 hours."
                        },
                        {
                            "label": "UV index",
                            "text": "Current UV index as a gauge with its level, the course over the next hours and the daily maximum."
                        },
                        {
                            "label": "Comfort (dew, pressure)",
                            "text": "Dew point scale from dry to oppressive, plus feels-like temperature, humidity, pressure with tendency and visibility."
                        },
                        {
                            "label": "Frost and ice",
                            "text": "Freezing level as a mountain graphic with your own altitude, lowest temperature of the next 12 hours, dew point, new snow and a frost and black ice indicator."
                        },
                        {
                            "label": "All values",
                            "text": "All values in a compact list, including values the other pages do not show, such as CAPE and the freezing level."
                        },
                        {
                            "label": "Warnings",
                            "text": "Black ice, thunder, hail and storm for the next 24 hours, each with the level none, possible, likely or severe."
                        }
                    ]
                },
                {
                    "id": "navigation",
                    "heading": "Navigation",
                    "paragraphs": [
                        "Press UP or DOWN, or swipe up or down, to change the page. START does not change the page. It selects a day on the 7-day page.",
                        "Open the menu with the MENU button. On 5-button watches hold UP. On touch watches you can also press and hold the screen."
                    ]
                },
                {
                    "id": "day-detail",
                    "heading": "Hourly detail for a day",
                    "paragraphs": [
                        "Each day on the 7-day page has a detail view. It shows the day hour by hour with time, symbol, temperature and wind, plus a chart with cloud cover and rain chance. Devices with enough memory also show UV."
                    ],
                    "steps": [
                        "Touch watches: tap the day on the [watch]7 Days[/watch] page. The detail view opens directly.",
                        "Button watches: press START on the [watch]7 Days[/watch] page. A selection box appears.",
                        "Move the box with UP and DOWN.",
                        "Press START to open the selected day. BACK closes the box.",
                        "In the detail view, UP and DOWN or a swipe left or right move one hour. At the end of a day you continue into the next day.",
                        "START or a swipe up jumps to the next day. A swipe down goes back one day.",
                        "Press BACK to return to the 7-day page."
                    ]
                },
                {
                    "id": "weather-models",
                    "heading": "Weather model",
                    "paragraphs": [
                        "Choose the forecast model with [set]Weather model[/set] in the app settings or in the watch menu. The app loads new data after a change.",
                        "Regional models only cover their own area. Outside that area use [set]Best match[/set] or a global model."
                    ],
                    "options": [
                        {
                            "label": "Best match",
                            "text": "Open-Meteo combines the most suitable models for your location. This is the default and a good choice everywhere."
                        },
                        {
                            "label": "ICON",
                            "text": "Model family of the German Weather Service (DWD) with worldwide coverage."
                        },
                        {
                            "label": "ECMWF",
                            "text": "Global model of the European Centre for Medium-Range Weather Forecasts."
                        },
                        {
                            "label": "GFS",
                            "text": "Global model of the US weather service NOAA."
                        },
                        {
                            "label": "ICON EU",
                            "text": "DWD regional model for Europe with a finer grid of about 7 km."
                        },
                        {
                            "label": "ICON D2",
                            "text": "DWD high-resolution model with about 2 km grid for Germany and neighbouring countries. It only forecasts about two days ahead."
                        }
                    ]
                },
                {
                    "id": "next-6h",
                    "heading": "Next 6 h page",
                    "paragraphs": [
                        "The [watch]Next 6h[/watch] page needs forecast data in 15-minute steps. Open-Meteo has this data only where ICON-D2 (central Europe) or HRRR (North America) cover your location.",
                        "Everywhere else the page shows [watch]No data[/watch]. Hide it with [set]None (hidden)[/set] in the page layout.",
                        "The page needs a device with enough memory. Low-memory watches and Instinct models with 1-bit display do not have it."
                    ]
                },
                {
                    "id": "warnings",
                    "heading": "Warnings",
                    "paragraphs": [
                        "Atmos Weather calculates warnings for black ice, thunder, hail and storm from thresholds on the hourly forecast. [b]They are not official weather warnings.[/b]",
                        "The [watch]Warnings[/watch] page shows each type with the level none, possible, likely or severe. The current page shows the most important one in its footer, for example [watch]Thunder in 3h[/watch].",
                        "When severe weather is possible, always check the warnings of your national weather service."
                    ]
                },
                {
                    "id": "glance-complications",
                    "heading": "Glance and complications",
                    "paragraphs": [
                        "On watches with Connect IQ 4 or newer, Atmos Weather appears in the glance list. [set]Glance content[/set] selects what the glance shows.",
                        "On watches with complication support the app publishes complications for watch faces. [watch]AtmosWeather[/watch] opens the app from the watch face. [watch]AtmosWeather 1[/watch] to [watch]AtmosWeather 3[/watch] carry one value each. Choose the value with [set]Complication 1[/set] to [set]Complication 3[/set] or in the watch menu under [watch]Complications[/watch].",
                        "The background service updates glance and complications also when the app is closed."
                    ],
                    "options": [
                        {
                            "label": "Glance content",
                            "text": "Temperature; Temperature, wind, rain; Precipitation ahead; Wind and gusts; UV index; Today min/max; Temperature trend."
                        },
                        {
                            "label": "Complication values",
                            "text": "Off, Temperature, Feels like, Rain chance, Precipitation, Wind, Gusts, UV index, Today min/max. Defaults: Temperature, Rain chance, Wind."
                        }
                    ]
                },
                {
                    "id": "location-updates",
                    "heading": "Location and updates",
                    "paragraphs": [
                        "With [set]Automatic location[/set] on, the app takes the position from your phone (Garmin weather) or from the watch. [set]Location source[/set] decides which one wins: most recent, phone or watch.",
                        "Turn automatic location off to use fixed coordinates in [set]Latitude[/set] and [set]Longitude[/set].",
                        "Without any position the app starts a GPS fix. You can also start one with [watch]GPS fix[/watch] in the watch menu. Do this outdoors.",
                        "The app updates in the background. [set]Update interval in minutes[/set] sets how often, from 10 to 720 minutes. The default is 60. [watch]Force update[/watch] in the menu loads new data right away.",
                        "Weather data comes through your phone. Without a connection the app shows the last stored forecast."
                    ]
                },
                {
                    "id": "watch-menu",
                    "heading": "Watch menu",
                    "paragraphs": [
                        "The watch menu and the phone settings change the same values. The last change wins. Some items only appear on devices that support them."
                    ],
                    "options": [
                        {
                            "label": "Force update",
                            "text": "Loads new weather data now."
                        },
                        {
                            "label": "Weather model",
                            "text": "Same choice as in the app settings."
                        },
                        {
                            "label": "Glance content, Animations",
                            "text": "Glance layout and animation level."
                        },
                        {
                            "label": "Temperature unit, Wind unit, Precipitation unit, Pressure unit, Distance unit",
                            "text": "Units. Distance unit is the visibility unit."
                        },
                        {
                            "label": "24h chart",
                            "text": "Curve A (area), Curve B (line) and Background band of the 24h page."
                        },
                        {
                            "label": "Complications",
                            "text": "Values of Complication 1 to 3."
                        },
                        {
                            "label": "GPS fix",
                            "text": "Gets a fresh position by GPS. Only shown with automatic location on."
                        },
                        {
                            "label": "Troubleshooting",
                            "text": "Help if the pages stop scrolling."
                        }
                    ]
                },
                {
                    "id": "settings",
                    "heading": "Settings",
                    "paragraphs": [
                        "Change these in the Connect IQ app on your phone. Units, model, glance, animations, the 24h chart and complications are also in the watch menu."
                    ],
                    "options": [
                        {
                            "label": "Weather model",
                            "text": "Best match, ICON, ECMWF, GFS, ICON EU, ICON D2."
                        },
                        {
                            "label": "Temperature unit",
                            "text": "Automatic (device), Celsius, Fahrenheit."
                        },
                        {
                            "label": "Wind unit",
                            "text": "Automatic (device), m/s, km/h, mph, Knots, Beaufort."
                        },
                        {
                            "label": "Precipitation unit",
                            "text": "Automatic (device), Millimeters, Inches."
                        },
                        {
                            "label": "Pressure unit, Visibility unit",
                            "text": "hPa or inHg, Kilometers or Miles. Only on devices with the extra pages."
                        },
                        {
                            "label": "Glance content",
                            "text": "What the glance shows."
                        },
                        {
                            "label": "Animations",
                            "text": "Full (rain, wind, clouds), Reduced (charts only) or Off."
                        },
                        {
                            "label": "24h curve A (area), 24h curve B (line), 24h background band",
                            "text": "Metrics of the 24h chart: Temperature, Feels like, Wind, Gusts, Precipitation, Rain chance, Cloud cover, Humidity or Off. The band only offers Rain chance, Cloud cover and Humidity. Defaults: Temperature, Wind, Rain chance."
                        },
                        {
                            "label": "Page 1 to Page 11",
                            "text": "Which view sits on which page, or None (hidden)."
                        },
                        {
                            "label": "Complication 1 to Complication 3",
                            "text": "Values for watch faces."
                        },
                        {
                            "label": "Automatic location, Location source, Latitude, Longitude",
                            "text": "Where the forecast is for."
                        },
                        {
                            "label": "Update interval in minutes",
                            "text": "Background update interval, 10 to 720 minutes."
                        }
                    ]
                },
                {
                    "id": "devices",
                    "heading": "Device differences",
                    "options": [
                        {
                            "label": "Devices with enough memory",
                            "text": "All 11 pages, configurable 24h chart, animations, glance on Connect IQ 4 or newer and complications where the watch supports them."
                        },
                        {
                            "label": "Low-memory watches",
                            "text": "For example fenix 5 and 6 (non-Pro), Forerunner 245, 55, 645 and 935, Venu Sq, vivoactive 3. Pages: Current, 24h, 7 Days and Warnings."
                        },
                        {
                            "label": "Instinct with 1-bit display",
                            "text": "Instinct 2, 2S, 2X, Crossover, Instinct 3 Solar 45 mm, Instinct E and Descent G1. Pages: Current, 24h, 7 Days and Wind. Warnings appear as a line on the current page. The menu is shorter."
                        }
                    ]
                },
                {
                    "id": "troubleshooting",
                    "heading": "Troubleshooting",
                    "paragraphs": [
                        "If the pages stop scrolling, a soft reboot of the watch usually helps. The menu item [watch]Troubleshooting[/watch] shows the same steps."
                    ],
                    "steps": [
                        "5-button watches: hold START/STOP for about 30 seconds until the watch turns off.",
                        "Press START/STOP for 1 to 2 seconds to turn it on again.",
                        "2-button watches: do the same with the upper right button.",
                        "Open Atmos Weather again."
                    ]
                }
            ]
        },
        "bierwandern": {
            "tabLabel": "App guide",
            "title": "Bierwandern guide",
            "intro": "Bierwandern records your hike as a Garmin activity and counts the drinks you have along the way. On the watch the app is called [watch]Beer Hike[/watch], in German [watch]Bierwanderung[/watch]. It also shows a very rough blood alcohol estimate. Read the section about the estimate before you use it.",
            "sections": [
                {
                    "id": "start",
                    "heading": "Start, pause and save",
                    "steps": [
                        "Open the app and wait until [watch]GPS[/watch] replaces [watch]GPS...[/watch].",
                        "Press START to begin the activity.",
                        "To pause, press START on the [watch]OVERVIEW[/watch] or [watch]BAC[/watch] page, or press BACK.",
                        "On the pause screen, START or a tap on the upper half continues ([watch]Resume[/watch]). BACK or a tap on the lower half ends the hike ([watch]Stop[/watch]).",
                        "On the end screen, START or a tap on the upper half saves the activity ([watch]Save[/watch]). BACK or a tap on the lower half deletes it ([watch]Discard[/watch])."
                    ]
                },
                {
                    "id": "pages",
                    "heading": "Pages",
                    "paragraphs": [
                        "Change pages with UP and DOWN or with a swipe up or down. Dots at the bottom show where you are."
                    ],
                    "options": [
                        {
                            "label": "OVERVIEW",
                            "text": "Time of day, distance, steps, current estimated BAC, your drinks so far and the time since the last drink."
                        },
                        {
                            "label": "DRINKS",
                            "text": "Beer, Wine and Schnapps. Here you add or remove drinks."
                        },
                        {
                            "label": "BAC",
                            "text": "Current estimate in per mille, the peak value [watch]Max[/watch] and a chart for the whole hike. The page always shows [watch]Very rough estimate![/watch]."
                        }
                    ]
                },
                {
                    "id": "drinks",
                    "heading": "Drink types",
                    "paragraphs": [
                        "The overview shows beer as a number of reference glasses. [set]Beer reference size[/set] sets that glass. Example: with 0,50 L, two beers of 0.25 L show as 1.0.",
                        "Wine and schnapps are counted per glass with fixed volumes."
                    ],
                    "options": [
                        {
                            "label": "Beer",
                            "text": "0.25 L, 0.33 L or 0.50 L. Alcohol from [set]Beer alcohol %[/set], default 5 %."
                        },
                        {
                            "label": "Wine",
                            "text": "Each wine counts as 0.1 L. Alcohol from [set]Wine alcohol %[/set], default 12 %."
                        },
                        {
                            "label": "Schnapps",
                            "text": "Each schnapps counts as 4 cl. Alcohol from [set]Schnapps alcohol %[/set], default 40 %."
                        }
                    ]
                },
                {
                    "id": "controls-buttons",
                    "heading": "Adding drinks with buttons",
                    "steps": [
                        "Go to the [watch]DRINKS[/watch] page.",
                        "Select Beer, Wine or Schnapps with UP and DOWN.",
                        "Press START to open the picker.",
                        "With the cursor on [watch]+[/watch], START adds one drink. For beer, move the cursor to a size with UP and DOWN and press START to change the size first.",
                        "MENU removes one drink of the selected type and size.",
                        "BACK closes the picker without a change. The watch vibrates briefly after each change."
                    ]
                },
                {
                    "id": "controls-touch",
                    "heading": "Adding drinks by touch",
                    "steps": [
                        "Swipe to the [watch]DRINKS[/watch] page and tap Beer, Wine or Schnapps.",
                        "Wine and schnapps: tap the upper half to add one, the lower half to remove one.",
                        "Beer: tap the [watch]+[/watch] area at the top to add, the [watch]-[/watch] area at the bottom to remove. Tap left or right in the middle to change the size.",
                        "While the picker is open, swipes do nothing. Press BACK to close it."
                    ]
                },
                {
                    "id": "menu",
                    "heading": "Menu",
                    "paragraphs": [
                        "Press MENU or press and hold the screen to open the settings menu. On 5-button watches hold UP. This works on every screen. The only exception is an open drink picker: there MENU removes a drink."
                    ]
                },
                {
                    "id": "settings",
                    "heading": "Settings",
                    "paragraphs": [
                        "You find these settings in the watch menu and in the Connect IQ app on your phone."
                    ],
                    "options": [
                        {
                            "label": "Use GPS",
                            "text": "Records your track with GPS. On by default."
                        },
                        {
                            "label": "Beer reference size",
                            "text": "0,25 L, 0,33 L or 0,50 L. Sets the glass for the beer count on the overview. Default 0,50 L."
                        },
                        {
                            "label": "Beer alcohol %",
                            "text": "Default 5 %. The watch menu offers 3 to 10 %."
                        },
                        {
                            "label": "Wine alcohol %",
                            "text": "Default 12 %. The watch menu offers 8 to 15 %."
                        },
                        {
                            "label": "Schnapps alcohol %",
                            "text": "Default 40 %. The watch menu offers 20 to 60 %."
                        }
                    ]
                },
                {
                    "id": "bac",
                    "heading": "How the BAC estimate works",
                    "paragraphs": [
                        "The app estimates your blood alcohol in per mille (‰) with the Widmark formula.",
                        "Alcohol in grams = volume in litres × alcohol % × 7.89.",
                        "BAC = alcohol in grams ÷ (body weight in kg × r), minus 0.12 ‰ for every hour since your first drink. r is 0.68 for men and 0.55 for women.",
                        "Weight and sex come from your Garmin user profile. Without a weight in the profile the app uses 75 kg.",
                        "The app treats each drink as fully absorbed the moment you log it. In reality alcohol enters the blood over time. Food, medication, health and your own metabolism change the real value a lot.",
                        "[b]The value is a very rough estimate, not a measurement. Never use it to decide whether you can drive, ride a bike or operate machines. If you drink, do not drive.[/b]"
                    ]
                },
                {
                    "id": "garmin-connect",
                    "heading": "Data in Garmin Connect",
                    "paragraphs": [
                        "When you save the activity, Garmin Connect shows these extra fields."
                    ],
                    "options": [
                        {
                            "label": "BAC",
                            "text": "Estimated value over time as a chart, in per mille."
                        },
                        {
                            "label": "Drink event",
                            "text": "Marks when you logged a drink: 1 beer, 2 wine, 3 schnapps."
                        },
                        {
                            "label": "Total beer, Total wine, Total schnapps",
                            "text": "Number of drinks per type, as a chart over time and as a total in the summary."
                        },
                        {
                            "label": "Peak BAC",
                            "text": "Highest estimated value of the hike."
                        }
                    ]
                }
            ]
        },
        "horizon": {
            "tabLabel": "Views",
            "title": "Horizon views",
            "intro": "Below the time, the arc view shows one of several views. Choose it in [set]✣ Arc View[/set]. Views marked ♛ are PRO.",
            "sections": [
                {
                    "id": "arc2",
                    "heading": "Second arc view",
                    "paragraphs": [
                        "With [set]Arc View 2 [♛ PRO][/set] you set a second view. [set]Arcview Switch Options [♛ PRO][/set] decides when it appears:"
                    ],
                    "options": [
                        {
                            "label": "Do not switch",
                            "text": "Only the first arc view is shown."
                        },
                        {
                            "label": "Switch on second wrist move",
                            "text": "Raise the wrist, lower it and raise it again within a few seconds."
                        },
                        {
                            "label": "Switch 4 seconds after wrist move",
                            "text": "The second view appears automatically 4 seconds after you raise the wrist."
                        },
                        {
                            "label": "Switch on sunrise/set",
                            "text": "The first view is shown during the day, the second at night."
                        }
                    ]
                },
                {
                    "id": "arcslots",
                    "heading": "Arc slots",
                    "paragraphs": [
                        "Set [set]✣ Arc View[/set] to [b]Arcslot options 1-3[/b], then choose the values in [set]✣ Arcslot Top[/set], [set]✣ Arcslot Left[/set] and [set]✣ Arcslot Right[/set]. If only the top slot is set and the other two are empty, the top value is shown with an icon."
                    ]
                },
                {
                    "id": "moon",
                    "heading": "Moon info",
                    "options": [
                        {
                            "label": "S",
                            "text": "Visible size of the moon: 100 % is the closest the moon gets to Earth."
                        },
                        {
                            "label": "I",
                            "text": "Illumination: how much of the visible moon is lit."
                        },
                        {
                            "label": "3:45 NE",
                            "text": "Moonrise time and compass direction."
                        },
                        {
                            "label": "6:47 NW",
                            "text": "Moonset time and compass direction."
                        },
                        {
                            "label": "27d",
                            "text": "Moon age: days since the new moon."
                        }
                    ]
                },
                {
                    "id": "forecast",
                    "heading": "Daily forecast",
                    "paragraphs": [
                        "Shows the next days, starting with tomorrow. Only with Garmin weather. The view has two pages: page 1 stays for about 3 seconds, page 2 for about 5 seconds."
                    ],
                    "options": [
                        {
                            "label": "Sa / Su",
                            "text": "Day of the week"
                        },
                        {
                            "label": "14%",
                            "text": "Chance of precipitation"
                        },
                        {
                            "label": "59–69°F",
                            "text": "Lowest and highest temperature"
                        }
                    ]
                },
                {
                    "id": "bodystatus",
                    "heading": "Body status",
                    "options": [
                        {
                            "label": "Outer ring",
                            "text": "Current Body Battery"
                        },
                        {
                            "label": "Inner ring",
                            "text": "Current stress level"
                        },
                        {
                            "label": "40",
                            "text": "Average resting heart rate of the last 7 days"
                        }
                    ]
                },
                {
                    "id": "movegoals",
                    "heading": "Move goals",
                    "options": [
                        {
                            "label": "Outer ring",
                            "text": "Steps"
                        },
                        {
                            "label": "2nd ring",
                            "text": "Calories burned"
                        },
                        {
                            "label": "Middle ring",
                            "text": "Floors climbed"
                        },
                        {
                            "label": "Inner ring",
                            "text": "Active minutes of the week"
                        }
                    ]
                },
                {
                    "id": "charts",
                    "heading": "Charts",
                    "paragraphs": [
                        "The chart runs from the oldest value on the left to the newest on the right. The tick marks on the time axis divide it into sections of roughly 20 minutes."
                    ],
                    "options": [
                        {
                            "label": "Left value",
                            "text": "Lowest value of the period"
                        },
                        {
                            "label": "Right value",
                            "text": "Highest value of the period"
                        }
                    ]
                },
                {
                    "id": "tides",
                    "heading": "Tides",
                    "paragraphs": [
                        "Choose [b]Local Tide Data [♛ PRO] [Stormglass-Key required][/b] as arc view and enter your key in [set]StormGlass Apikey (Tide Data)[/set]. [link=/guides/tides]Set up tides[/link]",
                        "Each bar is one hour, starting with the current hour on the left. Every third bar is outlined and labeled with the time. Depending on the watch, 11 to 14 hours are shown."
                    ],
                    "options": [
                        {
                            "label": "-0.1 / 0.1",
                            "text": "Lowest and highest tide level of the loaded data"
                        },
                        {
                            "label": "454 km",
                            "text": "Distance to the tide station"
                        }
                    ]
                },
                {
                    "id": "airquality",
                    "heading": "Air quality",
                    "paragraphs": [
                        "Choose [b]Local Airquality [♛ PRO] [OWM-Key required][/b] as arc view. It needs an OpenWeatherMap key in [set]Weather ApiKey[/set].",
                        "The view has two pages: NO2, PM10, O3 and PM2.5, then CO, SO2, NH3 and NO. The color of each circle shows the concentration level of that pollutant, from green (low) to red (high)."
                    ]
                },
                {
                    "id": "devices",
                    "heading": "Device differences",
                    "paragraphs": [
                        "Instinct 2, 2S, 2X, Crossover, Instinct 3 and Instinct E have no tide, air quality, stress chart or 7-day chart views.",
                        "On touch watches with Connect IQ 4.2 or later, tapping a data field opens the matching Garmin widget."
                    ]
                }
            ]
        },
        "mission": {
            "tabLabel": "Second view",
            "title": "Second view",
            "intro": "Instinct Mission can show a second set of views after a second wrist movement.",
            "sections": [
                {
                    "id": "exp",
                    "heading": "Switch with a second wrist movement",
                    "steps": [
                        "Raise your wrist. The watch wakes up and shows the seconds.",
                        "Turn the wrist away and raise it again within about 9 seconds.",
                        "The views with a second setting switch. The next double movement switches back."
                    ],
                    "paragraphs": [
                        "Only areas with a second setting change: [set]♛ Sunline 2 options (shows up after 2nd wristmove)[/set], [set]♛ Data under Sunline 2 (shows up after 2nd wristmove)[/set] and [set]✣♛ Bottom View 2 (shows up after 2nd wristmove)[/set].",
                        "On AMOLED watches with touch (Connect IQ 4.2 or later), tapping the time also switches.",
                        "This is a PRO feature. Without a key it is locked 5 days after the last watchface update."
                    ]
                }
            ]
        },
        "centurion": {
            "tabLabel": "Views",
            "title": "Centurion views",
            "intro": "The bottom section of Centurion shows charts, weather, tides and more. Choose the view in the watchface settings.",
            "sections": [
                {
                    "id": "charts",
                    "heading": "Chart values",
                    "paragraphs": [
                        "Elevation values are shown in thousands (k)."
                    ],
                    "options": [
                        {
                            "label": "Left",
                            "text": "Time of the oldest value in the chart. Only shown when the chart is in the bottom view."
                        },
                        {
                            "label": "Middle",
                            "text": "Lowest and highest value"
                        },
                        {
                            "label": "Right",
                            "text": "Average of the period (Ø)"
                        }
                    ]
                },
                {
                    "id": "weather",
                    "heading": "♛ Weather view",
                    "options": [
                        {
                            "label": "Left",
                            "text": "Weather condition icon and chance of precipitation"
                        },
                        {
                            "label": "Top middle",
                            "text": "Current temperature"
                        },
                        {
                            "label": "2nd row",
                            "text": "Lowest and highest temperature of the day"
                        },
                        {
                            "label": "3rd row",
                            "text": "Wind speed, the icon on the right shows the wind direction"
                        },
                        {
                            "label": "Bottom middle",
                            "text": "Time of the observation"
                        },
                        {
                            "label": "Right",
                            "text": "Humidity"
                        }
                    ]
                },
                {
                    "id": "more",
                    "heading": "More views",
                    "paragraphs": [
                        "[b]♛ Tide View[/b] needs a Stormglass key. [link=/guides/tides]Set up tides[/link]",
                        "7-day charts for steps, ♛ active calories and ♛ active minutes.",
                        "[set]♛ Bottom Section 2 (toggles)[/set] shows a second bottom view after a second wrist movement. [link=/guides/switchviews]Switchviews[/link]",
                        "The air quality index needs the app Atmos Air Quality on the watch."
                    ]
                }
            ]
        },
        "zenith": {
            "tabLabel": "Planets",
            "title": "Planets",
            "intro": "Zenith shows the planets as icons moving along the sky arc, between their rise and set.",
            "sections": [
                {
                    "id": "planets",
                    "heading": "Planets on the sky arc",
                    "paragraphs": [
                        "Mercury, Venus, Mars, Jupiter, Saturn, Uranus and Neptune each have their own icon. They move along the arc from rise to set.",
                        "The planet positions are not calculated on the watch. They are downloaded for about the next 7 days whenever the phone is connected.",
                        "The black and white icons are used on Instinct watches."
                    ]
                },
                {
                    "id": "settings",
                    "heading": "Planets display options",
                    "paragraphs": [
                        "On AMOLED watches you choose in [set]Planets display options[/set]:"
                    ],
                    "options": [
                        {
                            "label": "Show planets all the time (in real time)",
                            "text": "Default. Planets are shown day and night."
                        },
                        {
                            "label": "Show planets during night time only (in real time)",
                            "text": "Planets are only shown after sunset."
                        },
                        {
                            "label": "Sun and Moon only",
                            "text": "Only sun and moon move along the arc."
                        },
                        {
                            "label": "None",
                            "text": "No sky objects on the arc."
                        }
                    ]
                },
                {
                    "id": "more",
                    "heading": "More views",
                    "paragraphs": [
                        "Switchview 2 (second wrist movement, or a long press on the minutes on touch watches), tide chart, hourly forecast and world map with MGRS coordinates. [link=/guides/switchviews]Switchviews[/link]"
                    ]
                }
            ]
        }
    }
};
export default products;
